import React, { useState, useEffect } from 'react';
import {
  Users, Layers, Bell, FileText, Award, TrendingUp,
  Package, Zap, RefreshCw, AlertCircle, Activity,
  BarChart2, Shield,
} from 'lucide-react';
import UsersService from '../components/home/homeservice';

const API_BASE_URL = 'https://api.jamboricg.com';

const apiGet = async (path) => {
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Token ${token}` } : {}),
    },
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json();
};

const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString());

// ── Atoms ──────────────────────────────────────────────────────────────────────

const Skeleton = ({ w = 'w-16', h = 'h-6' }) => (
  <div className={`${h} ${w} bg-slate-100 rounded animate-pulse`} />
);

const StatCard = ({ icon: Icon, label, value, sub, loading }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</span>
      <div className="w-8 h-8 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center">
        <Icon size={15} className="text-slate-400" />
      </div>
    </div>
    {loading
      ? <Skeleton h="h-8" w="w-24" />
      : <p className="text-3xl font-bold text-slate-800 leading-none tracking-tight">{fmt(value)}</p>
    }
    {sub && !loading && <p className="text-xs text-slate-400">{sub}</p>}
  </div>
);

const Row = ({ label, value, loading, accent = false }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-slate-50 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    {loading
      ? <Skeleton w="w-10" h="h-4" />
      : <span className={`text-sm font-bold ${accent ? 'text-teal-600' : 'text-slate-700'}`}>{fmt(value)}</span>
    }
  </div>
);

const Panel = ({ title, badge, children }) => (
  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
      <h3 className="text-sm font-bold text-slate-700">{title}</h3>
      {badge && <span className="text-xs text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2.5 py-0.5">{badge}</span>}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Tag = ({ label, value, color = 'slate' }) => {
  const cls = {
    slate:  'bg-slate-50 text-slate-600 border-slate-200',
    teal:   'bg-teal-50 text-teal-700 border-teal-100',
    amber:  'bg-amber-50 text-amber-700 border-amber-100',
    red:    'bg-red-50 text-red-700 border-red-100',
    green:  'bg-emerald-50 text-emerald-700 border-emerald-100',
  }[color] || 'bg-slate-50 text-slate-600 border-slate-200';
  return (
    <div className={`flex items-center justify-between rounded-lg border px-3 py-2 ${cls}`}>
      <span className="text-xs font-semibold">{label}</span>
      <span className="text-sm font-bold">{fmt(value)}</span>
    </div>
  );
};

// ── Main ───────────────────────────────────────────────────────────────────────
export default function Home() {
  const [loadingUsers,         setLoadingUsers]         = useState(true);
  const [loadingGangs,         setLoadingGangs]         = useState(true);
  const [loadingMemberships,   setLoadingMemberships]   = useState(true);
  const [loadingServiceReqs,   setLoadingServiceReqs]   = useState(true);
  const [loadingSponsor,       setLoadingSponsor]       = useState(true);
  const [loadingCFN,           setLoadingCFN]           = useState(true);
  const [loadingUvunajiInv,    setLoadingUvunajiInv]    = useState(true);
  const [loadingUvunajiAssets, setLoadingUvunajiAssets] = useState(true);

  const [userCount,       setUserCount]       = useState(null);
  const [gangStats,       setGangStats]       = useState(null);
  const [memberStats,     setMemberStats]     = useState(null);
  const [serviceStats,    setServiceStats]    = useState(null);
  const [sponsorStats,    setSponsorStats]    = useState(null);
  const [cfnStats,        setCfnStats]        = useState(null);
  const [uvunajiInvStats, setUvunajiInvStats] = useState(null);
  const [uvunajiAssets,   setUvunajiAssets]   = useState(null);
  const [lastRefreshed,   setLastRefreshed]   = useState(new Date());
  const [errors,          setErrors]          = useState([]);

  const addError = (msg) => setErrors(prev => [...prev.slice(-4), msg]);

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const data = await UsersService.getAllUsers();
      setUserCount(Array.isArray(data) ? data.length : (data?.count ?? 0));
    } catch (e) { addError('Users: ' + e.message); }
    finally { setLoadingUsers(false); }
  };

  const fetchGangs = async () => {
    setLoadingGangs(true);
    try {
      const data  = await apiGet('/api/majanga/community-gangs/');
      const gangs = Array.isArray(data) ? data : (data?.results ?? []);
      setGangStats({
        total:         gangs.length,
        totalContribs: gangs.reduce((s, g) => s + (g.total_contribution || 0), 0),
        private:       gangs.filter(g => g.is_private).length,
        public:        gangs.filter(g => !g.is_private).length,
        totalMembers:  gangs.reduce((s, g) => s + (g.member_count || 0), 0),
      });
    } catch (e) { addError('Gangs: ' + e.message); }
    finally { setLoadingGangs(false); }
  };

  const fetchMemberships = async () => {
    setLoadingMemberships(true);
    try {
      const [p, a] = await Promise.all([
        apiGet('/api/majanga/gang-memberships/?status=pending'),
        apiGet('/api/majanga/gang-memberships/?status=approved'),
      ]);
      setMemberStats({
        pending:  p?.count  ?? (Array.isArray(p) ? p.length : 0),
        approved: a?.count  ?? (Array.isArray(a) ? a.length : 0),
      });
    } catch (e) { addError('Memberships: ' + e.message); }
    finally { setLoadingMemberships(false); }
  };

  const fetchServiceStats = async () => {
    setLoadingServiceReqs(true);
    try {
      const data = await apiGet('/api/majanga/service-requests/statistics-public/');
      setServiceStats({
        total:     data?.total_requests          ?? 0,
        pending:   data?.summary?.pending        ?? 0,
        approved:  data?.summary?.approved       ?? 0,
        rejected:  data?.summary?.rejected       ?? 0,
        completed: data?.summary?.completed      ?? 0,
        recent:    data?.recent_requests_30_days ?? 0,
      });
    } catch (e) { addError('Service Requests: ' + e.message); }
    finally { setLoadingServiceReqs(false); }
  };

  const fetchSponsorStats = async () => {
    setLoadingSponsor(true);
    try {
      const data = await apiGet('/api/majanga/sponsor-requests/admin-view/');
      const list = data?.requests ?? (Array.isArray(data) ? data : []);
      setSponsorStats({
        total:     data?.statistics?.total_requests ?? list.length,
        submitted: list.filter(r => r.status === 'submitted').length,
        approved:  list.filter(r => r.status === 'approved').length,
      });
    } catch {
      try {
        const data = await apiGet('/api/majanga/sponsor-requests/my-requests/');
        const list = data?.requests ?? [];
        setSponsorStats({ total: data?.count ?? list.length, submitted: 0, approved: 0 });
      } catch (e2) { addError('Sponsors: ' + e2.message); }
    } finally { setLoadingSponsor(false); }
  };

  const fetchCFNStats = async () => {
    setLoadingCFN(true);
    try {
      const data = await apiGet('/api/majanga/cfn-forms/stats/');
      setCfnStats({ total: data?.total_forms ?? 0, recent: data?.recent_registrations_30_days ?? 0 });
    } catch (e) { addError('CFN: ' + e.message); }
    finally { setLoadingCFN(false); }
  };

  const fetchUvunajiInvestments = async () => {
    setLoadingUvunajiInv(true);
    try {
      const data        = await apiGet('/uvunaji/api/v1/admin/investments/');
      const investments = Array.isArray(data) ? data : [];
      const active      = investments.filter(i => i.is_active);
      const byAsset     = {};
      investments.forEach(inv => {
        const name = inv.asset_name || 'Unknown';
        if (!byAsset[name]) byAsset[name] = { count: 0, active: 0 };
        byAsset[name].count++;
        if (inv.is_active) byAsset[name].active++;
      });
      setUvunajiInvStats({
        total:           investments.length,
        active:          active.length,
        inactive:        investments.length - active.length,
        uniqueInvestors: new Set(investments.map(i => i.user_email)).size,
        topAssets: Object.entries(byAsset)
          .sort((a, b) => b[1].count - a[1].count)
          .slice(0, 5)
          .map(([name, s]) => ({ name, ...s })),
      });
    } catch (e) { addError('Uvunaji Investments: ' + e.message); }
    finally { setLoadingUvunajiInv(false); }
  };

  const fetchUvunajiAssets = async () => {
    setLoadingUvunajiAssets(true);
    try {
      const data   = await apiGet('/uvunaji/api/v1/assets/');
      const assets = Array.isArray(data) ? data : [];
      setUvunajiAssets({
        total:      assets.length,
        totalValue: assets.reduce((s, a) => s + parseFloat(a.investing_price || 0) * (a.available_quantity || 0), 0),
        categories: new Set(assets.map(a => a.category)).size,
        list:       assets.slice(0, 5),
      });
    } catch (e) { addError('Uvunaji Assets: ' + e.message); }
    finally { setLoadingUvunajiAssets(false); }
  };

  const refreshAll = () => {
    setLastRefreshed(new Date());
    setErrors([]);
    fetchUsers(); fetchGangs(); fetchMemberships();
    fetchServiceStats(); fetchSponsorStats(); fetchCFNStats();
    fetchUvunajiInvestments(); fetchUvunajiAssets();
  };

  useEffect(() => { refreshAll(); }, []);

  const anyLoading = [
    loadingUsers, loadingGangs, loadingMemberships,
    loadingServiceReqs, loadingSponsor, loadingCFN,
    loadingUvunajiInv, loadingUvunajiAssets,
  ].some(Boolean);

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── TOP HEADER ──────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-900 flex items-center justify-center">
            <Shield size={15} className="text-white" />
          </div>
          <div>
            <h1 className="text-sm font-bold text-slate-800">JamboRicG Dashboard</h1>
            <p className="text-xs text-slate-400">
              Last updated {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {anyLoading && (
            <div className="flex items-center gap-1.5 text-xs text-slate-400">
              <div className="w-1.5 h-1.5 rounded-full bg-teal-500 animate-pulse" />
              Loading…
            </div>
          )}
          <button
            onClick={refreshAll}
            disabled={anyLoading}
            className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-40"
          >
            <RefreshCw size={13} className={anyLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── BODY ────────────────────────────────────────────────────────────── */}
      <div className="px-6 py-6 space-y-8 max-w-screen-xl mx-auto">

        {/* Error notices */}
        {errors.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
            <AlertCircle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-800 space-y-0.5">
              {errors.map((e, i) => <div key={i}>{e}</div>)}
            </div>
          </div>
        )}

        {/* ── SECTION: COMMUNITY ──────────────────────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-sm font-bold text-slate-700">Community Overview</h2>
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400">Majanga</span>
          </div>

          {/* 4-col stat grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <StatCard icon={Users}    label="Registered Users"    value={userCount}             loading={loadingUsers} />
            <StatCard icon={Layers}   label="Community Gangs"     value={gangStats?.total}       loading={loadingGangs}
              sub={gangStats ? `${gangStats.public} public · ${gangStats.private} private` : undefined} />
            <StatCard icon={Users}    label="Total Members"        value={gangStats?.totalMembers} loading={loadingGangs} />
            <StatCard icon={Bell}     label="Pending Join Requests" value={memberStats?.pending}  loading={loadingMemberships}
              sub={memberStats ? `${memberStats.approved} approved` : undefined} />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <StatCard icon={FileText} label="Service Requests"    value={serviceStats?.total}   loading={loadingServiceReqs}
              sub={serviceStats ? `${serviceStats.recent} in last 30 days` : undefined} />
            <StatCard icon={Award}    label="Sponsor Requests"    value={sponsorStats?.total}   loading={loadingSponsor}
              sub={sponsorStats ? `${sponsorStats.submitted} pending` : undefined} />
            <StatCard icon={BarChart2} label="CFN Forms"          value={cfnStats?.total}       loading={loadingCFN}
              sub={cfnStats ? `${cfnStats.recent} new this month` : undefined} />
            <StatCard icon={Activity} label="Total Contributions" value={gangStats ? `TZS ${fmt(Math.round(gangStats.totalContribs))}` : null}
              loading={loadingGangs} />
          </div>

          {/* Breakdown panels */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            <Panel title="Service Requests" badge="breakdown">
              <div className="grid grid-cols-2 gap-2 mb-4">
                <Tag label="Pending"   value={serviceStats?.pending}   color="amber" />
                <Tag label="Approved"  value={serviceStats?.approved}  color="green" />
                <Tag label="Completed" value={serviceStats?.completed} color="teal"  />
                <Tag label="Rejected"  value={serviceStats?.rejected}  color="red"   />
              </div>
              {!loadingServiceReqs && serviceStats?.total > 0 && (
                <div className="space-y-2 pt-1">
                  {[
                    { label: 'Pending',   count: serviceStats.pending,   color: '#f59e0b' },
                    { label: 'Approved',  count: serviceStats.approved,  color: '#10b981' },
                    { label: 'Completed', count: serviceStats.completed, color: '#0d9488' },
                    { label: 'Rejected',  count: serviceStats.rejected,  color: '#ef4444' },
                  ].map(r => (
                    <div key={r.label} className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 w-16 shrink-0">{r.label}</span>
                      <div className="flex-1 bg-slate-100 rounded-full h-1">
                        <div
                          className="h-1 rounded-full transition-all duration-700"
                          style={{ width: `${(r.count / serviceStats.total) * 100}%`, background: r.color }}
                        />
                      </div>
                      <span className="text-xs font-bold text-slate-600 w-5 text-right">{r.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Communities & Memberships">
              <Row label="Total Gangs"          value={gangStats?.total}       loading={loadingGangs} />
              <Row label="Public Communities"   value={gangStats?.public}      loading={loadingGangs} accent />
              <Row label="Private Communities"  value={gangStats?.private}     loading={loadingGangs} />
              <Row label="Pending Join Requests" value={memberStats?.pending}  loading={loadingMemberships} />
              <Row label="Approved Members"     value={memberStats?.approved}  loading={loadingMemberships} accent />
              <Row label="Sponsor Requests"     value={sponsorStats?.total}    loading={loadingSponsor} />
              <Row label="CFN Registrations"    value={cfnStats?.total}        loading={loadingCFN} />
            </Panel>

          </div>
        </section>

        {/* ── SECTION: UVUNAJI ────────────────────────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-sm font-bold text-slate-700">Uvunaji Investments</h2>
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs text-slate-400">Investment platform</span>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <StatCard icon={TrendingUp} label="Total Investments"  value={uvunajiInvStats?.total}           loading={loadingUvunajiInv} />
            <StatCard icon={Zap}        label="Active Investments" value={uvunajiInvStats?.active}          loading={loadingUvunajiInv}
              sub={uvunajiInvStats ? `${uvunajiInvStats.inactive} closed` : undefined} />
            <StatCard icon={Users}      label="Unique Investors"   value={uvunajiInvStats?.uniqueInvestors} loading={loadingUvunajiInv} />
            <StatCard icon={Package}    label="Asset Types"        value={uvunajiAssets?.total}             loading={loadingUvunajiAssets}
              sub={uvunajiAssets ? `${uvunajiAssets.categories} categories` : undefined} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

            {/* Investment status */}
            <Panel title="Investment Status">
              {!loadingUvunajiInv && uvunajiInvStats?.total > 0 && (
                <div className="mb-4">
                  <div className="flex justify-between text-xs text-slate-400 mb-1.5">
                    <span>Active — {uvunajiInvStats.active}</span>
                    <span>Closed — {uvunajiInvStats.inactive}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                    <div
                      className="h-full bg-slate-800 transition-all duration-700"
                      style={{ width: `${(uvunajiInvStats.active / uvunajiInvStats.total) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-slate-400 mt-1 text-right">
                    {Math.round((uvunajiInvStats.active / uvunajiInvStats.total) * 100)}% active rate
                  </p>
                </div>
              )}
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Top Assets by Investments</p>
              {loadingUvunajiInv
                ? <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} w="w-full" h="h-8" />)}</div>
                : uvunajiInvStats?.topAssets?.length > 0
                  ? <div className="space-y-2">
                      {uvunajiInvStats.topAssets.map((a, i) => (
                        <div key={i} className="flex items-center gap-3 py-1">
                          <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 text-xs font-bold flex items-center justify-center flex-shrink-0">
                            {i + 1}
                          </span>
                          <span className="text-sm text-slate-700 flex-1 truncate">{a.name}</span>
                          <span className="text-xs font-semibold text-slate-500">{a.count} inv</span>
                          <span className="text-xs font-bold text-teal-600">{a.active} active</span>
                        </div>
                      ))}
                    </div>
                  : <p className="text-xs text-slate-400">No data</p>
              }
            </Panel>

            {/* Asset portfolio */}
            <Panel title="Asset Portfolio">
              <div className="bg-slate-50 border border-slate-100 rounded-lg p-3 mb-4">
                <p className="text-xs text-slate-400 mb-1">Total Available Portfolio Value</p>
                {loadingUvunajiAssets
                  ? <Skeleton h="h-7" w="w-36" />
                  : <p className="text-xl font-bold text-slate-800">
                      TZS {uvunajiAssets ? fmt(Math.round(uvunajiAssets.totalValue)) : '—'}
                    </p>
                }
                <p className="text-xs text-slate-400 mt-0.5">Remaining investable stock</p>
              </div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Available Assets</p>
              {loadingUvunajiAssets
                ? <div className="space-y-2">{[1,2,3,4].map(i => <Skeleton key={i} w="w-full" h="h-10" />)}</div>
                : uvunajiAssets?.list?.length > 0
                  ? <div className="space-y-3">
                      {uvunajiAssets.list.map((asset, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                            {asset.asset_image
                              ? <img src={`${API_BASE_URL}${asset.asset_image}`} alt="" className="w-full h-full object-cover rounded-lg" />
                              : <Package size={13} className="text-slate-400" />
                            }
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold text-slate-700 truncate">{asset.asset_name}</p>
                            <p className="text-xs text-slate-400">{asset.category}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <p className="text-xs font-bold text-slate-700">TZS {fmt(Number(asset.investing_price))}</p>
                            <p className="text-xs text-teal-600 font-medium">{asset.available_quantity} left</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  : <p className="text-xs text-slate-400">No assets found</p>
              }
            </Panel>

          </div>
        </section>

        {/* ── SECTION: PLATFORM SUMMARY ───────────────────────────────────── */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <h2 className="text-sm font-bold text-slate-700">Platform Summary</h2>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Metric</th>
                  <th className="text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Module</th>
                  <th className="text-right text-xs font-semibold text-slate-400 uppercase tracking-wider px-5 py-3">Value</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {[
                  { label: 'Registered Users',    module: 'Platform',  value: userCount,                    loading: loadingUsers },
                  { label: 'Community Gangs',     module: 'Majanga',   value: gangStats?.total,             loading: loadingGangs },
                  { label: 'Total Gang Members',  module: 'Majanga',   value: gangStats?.totalMembers,      loading: loadingGangs },
                  { label: 'Total Contributions', module: 'Majanga',   value: gangStats ? `TZS ${fmt(Math.round(gangStats.totalContribs))}` : null, loading: loadingGangs },
                  { label: 'Service Requests',    module: 'Majanga',   value: serviceStats?.total,          loading: loadingServiceReqs },
                  { label: 'Sponsor Requests',    module: 'Majanga',   value: sponsorStats?.total,          loading: loadingSponsor },
                  { label: 'CFN Registrations',   module: 'Majanga',   value: cfnStats?.total,              loading: loadingCFN },
                  { label: 'Total Investments',   module: 'Uvunaji',   value: uvunajiInvStats?.total,       loading: loadingUvunajiInv },
                  { label: 'Active Investments',  module: 'Uvunaji',   value: uvunajiInvStats?.active,      loading: loadingUvunajiInv },
                  { label: 'Unique Investors',    module: 'Uvunaji',   value: uvunajiInvStats?.uniqueInvestors, loading: loadingUvunajiInv },
                  { label: 'Asset Types',         module: 'Uvunaji',   value: uvunajiAssets?.total,         loading: loadingUvunajiAssets },
                  { label: 'Portfolio Value',     module: 'Uvunaji',   value: uvunajiAssets ? `TZS ${fmt(Math.round(uvunajiAssets.totalValue))}` : null, loading: loadingUvunajiAssets },
                ].map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3 text-slate-700 font-medium">{r.label}</td>
                    <td className="px-5 py-3">
                      <span className="text-xs text-slate-400 bg-slate-50 border border-slate-100 rounded-full px-2 py-0.5">{r.module}</span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      {r.loading
                        ? <div className="h-4 w-12 bg-slate-100 rounded animate-pulse ml-auto" />
                        : <span className="text-sm font-bold text-slate-800">{r.value != null ? r.value : '—'}</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <div className="text-center text-xs text-slate-300 pb-4">
          JamboRicG Admin · {new Date().getFullYear()}
        </div>

      </div>
    </div>
  );
}
